import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { DollarSign, ShoppingCart, Boxes, Users, Plus, Pencil, Trash2, Tag, Eye, Gift, Wallet, Sparkles, UploadCloud, X, Quote } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { productImage, onImgError, compressImageFile } from "@/lib/image";

const EMPTY = {
  name: "", category: "clones", strain_type: "hybrid", price: 200,
  thc: "", cbd: "<0.3% D9", flowering_time: "", genetics: "", lineage: "", breeder: "", description: "",
  image_url: "", pack_size: "", stock: 25, featured: false, categories: [],
};

const EMPTY_PROMO = { code: "", description: "", discount_percent: 25, min_clones: 2, active: true, expires_at: "", max_uses: "", once_per_customer: false };

const STATUSES = ["pending", "paid", "processing", "cutting", "rooted_ready", "shipped", "delivered", "cancelled", "refunded"];
const STATUS_LABELS = { pending: "Pending", paid: "Paid", processing: "Processing", cutting: "Cutting/Rooting", rooted_ready: "Rooted–Ready", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled", refunded: "Refunded" };
const TICKET_STATUSES = ["open", "responded", "closed"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("all");
  const [ticketProductFilter, setTicketProductFilter] = useState("all");
  const [viewTicket, setViewTicket] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [sending, setSending] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [orderFilter, setOrderFilter] = useState("all");
  const [dropTheme, setDropTheme] = useState("");
  const [dropSaving, setDropSaving] = useState(false);
  const [waitlist, setWaitlist] = useState({});
  const [notifyingId, setNotifyingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [imgUploadingId, setImgUploadingId] = useState(null);
  const [imgSearch, setImgSearch] = useState("");
  const [imgCat, setImgCat] = useState("all");
  const [adminCategories, setAdminCategories] = useState([]);
  const [catEditing, setCatEditing] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [catError, setCatError] = useState("");
  const [catSlugTouched, setCatSlugTouched] = useState(false);
  const [bulkCat, setBulkCat] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewEditing, setReviewEditing] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const openBulkTag = (c) => {
    const preselected = products
      .filter((p) => p.category === "clones" && (p.categories || []).includes(c.slug))
      .map((p) => p.id);
    setBulkCat(c);
    setBulkSelected(new Set(preselected));
    setBulkSearch("");
    setBulkOpen(true);
  };

  const toggleBulk = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveBulkTag = async () => {
    const slug = bulkCat.slug;
    const currentlyTagged = new Set(
      products.filter((p) => p.category === "clones" && (p.categories || []).includes(slug)).map((p) => p.id)
    );
    const add = [...bulkSelected].filter((id) => !currentlyTagged.has(id));
    const remove = [...currentlyTagged].filter((id) => !bulkSelected.has(id));
    if (add.length === 0 && remove.length === 0) { setBulkOpen(false); return; }
    setBulkSaving(true);
    try {
      const { data } = await api.post(`/admin/categories/${bulkCat.id}/bulk-tag`, { add, remove });
      toast.success(`${bulkCat.name}: +${data.tagged} tagged, −${data.untagged} removed`);
      setBulkOpen(false);
      load();
      loadCategories();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update tags.");
    } finally {
      setBulkSaving(false);
    }
  };

  const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const loadCategories = useCallback(() => {
    api.get("/admin/categories").then(({ data }) => setAdminCategories(data || [])).catch(() => {});
  }, []);

  const openNewCat = () => { setCatEditing({ name: "", slug: "", description: "", active: true, sort_order: "" }); setCatError(""); setCatSlugTouched(false); setCatOpen(true); };
  const openEditCat = (c) => { setCatEditing({ ...c }); setCatError(""); setCatSlugTouched(true); setCatOpen(true); };

  const saveCategory = async () => {
    const name = (catEditing.name || "").trim();
    if (!name) { setCatError("Category name is required."); return; }
    const body = {
      name,
      slug: slugify(catEditing.slug || catEditing.name),
      description: catEditing.description || "",
      active: catEditing.active,
    };
    if (catEditing.sort_order !== "" && catEditing.sort_order != null) body.sort_order = Number(catEditing.sort_order);
    try {
      if (catEditing.id) await api.put(`/admin/categories/${catEditing.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success(catEditing.id ? "Category updated" : "Category created");
      setCatOpen(false);
      loadCategories();
    } catch (err) {
      setCatError(formatApiError(err.response?.data?.detail) || "Could not save category.");
    }
  };

  const delCategory = async (c) => {
    if (!window.confirm(`Delete ${c.name}? ${c.clone_count} clone${c.clone_count === 1 ? "" : "s"} will lose this tag. This cannot be undone. (The clones themselves are NOT deleted.)`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success(`Deleted ${c.name}`);
      loadCategories();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleCatActive = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, { active: !c.active });
      loadCategories();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveCatOrder = async (c, value) => {
    const n = Number(value);
    if (isNaN(n) || n === c.sort_order) return;
    try {
      await api.put(`/admin/categories/${c.id}`, { sort_order: n });
      loadCategories();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadReviews = useCallback(() => {
    api.get("/admin/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  }, []);

  const openNewReview = () => { setReviewEditing({ type: "text", quote: "", author_handle: "", strain: "", rating: 5, display_order: "", is_active: true }); setReviewError(""); setReviewOpen(true); };
  const openEditReview = (r) => { setReviewEditing({ ...r }); setReviewError(""); setReviewOpen(true); };

  const saveReview = async () => {
    const quote = (reviewEditing.quote || "").trim();
    if (!quote) { setReviewError("A review quote is required."); return; }
    const body = {
      type: "text",
      quote,
      author_handle: (reviewEditing.author_handle || "").trim(),
      strain: (reviewEditing.strain || "").trim(),
      rating: reviewEditing.rating ? Number(reviewEditing.rating) : null,
      is_active: reviewEditing.is_active,
    };
    if (reviewEditing.display_order !== "" && reviewEditing.display_order != null) body.display_order = Number(reviewEditing.display_order);
    try {
      if (reviewEditing.id) await api.put(`/admin/reviews/${reviewEditing.id}`, body);
      else await api.post("/admin/reviews", body);
      toast.success(reviewEditing.id ? "Review updated" : "Review added");
      setReviewOpen(false);
      loadReviews();
    } catch (err) {
      setReviewError(formatApiError(err.response?.data?.detail) || "Could not save review.");
    }
  };

  const delReview = async (r) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleReviewActive = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { is_active: !r.is_active });
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveReviewOrder = async (r, value) => {
    const n = Number(value);
    if (isNaN(n) || n === r.display_order) return;
    try {
      await api.put(`/admin/reviews/${r.id}`, { display_order: n });
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadRowImage = async (product, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setImgUploadingId(product.id);
    try {
      const compressed = await compressImageFile(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/admin/products/upload-image", fd);
      await api.put(`/products/${product.id}`, { image_url: data.url });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, image_url: data.url } : p)));
      toast.success(`${product.name} image updated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setImgUploadingId(null);
    }
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setUploading(true);
    try {
      const compressed = await compressImageFile(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/admin/products/upload-image", fd);
      setEditing((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded & optimized");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const confirmPayment = async (orderId) => {
    try {
      await api.put(`/admin/orders/${orderId}/payment`, { payment_status: "received" });
      toast.success("Payment confirmed — customer notified");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const orderAgeDays = (createdAt) => {
    if (!createdAt) return 0;
    return (Date.now() - new Date(createdAt).getTime()) / 86400000;
  };

  const loadDrop = useCallback(() => {
    api.get("/admin/weekly-drop/settings").then(({ data }) => setDropTheme(data.theme_label)).catch(() => {});
    api.get("/admin/waitlist").then(({ data }) => setWaitlist(data || {})).catch(() => {});
  }, []);

  const notifyWaitlist = async (product) => {
    setNotifyingId(product.id);
    try {
      const { data } = await api.post(`/admin/waitlist/${product.id}/notify`);
      toast.success(data?.message || `Notified waitlist for ${product.name}.`);
      loadDrop();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setNotifyingId(null);
    }
  };

  const saveDropTheme = async () => {
    setDropSaving(true);
    try {
      const { data } = await api.put("/admin/weekly-drop/settings", { theme_label: dropTheme });
      setDropTheme(data.theme_label);
      toast.success("Week theme saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDropSaving(false);
    }
  };

  const toggleAvailability = async (product) => {
    const next = (product.availability || "preorder") === "ready" ? "preorder" : "ready";
    try {
      await api.put(`/products/${product.id}`, { availability: next });
      toast.success(`${product.name} → ${next === "ready" ? "Ready to Ship" : "Preorder"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveCap = async (product, value) => {
    const cap = value === "" ? null : Number(value);
    try {
      await api.put(`/products/${product.id}`, { max_preorders: cap });
      toast.success(`${product.name} cap ${cap == null ? "removed (unlimited)" : `set to ${cap}`}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const [rewardsSettings, setRewardsSettings] = useState(null);
  const [balances, setBalances] = useState([]);
  const [creditTxns, setCreditTxns] = useState([]);
  const [adjustUser, setAdjustUser] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNote, setAdjustNote] = useState("");

  const loadRewards = useCallback(() => {
    api.get("/admin/rewards/settings").then(({ data }) => setRewardsSettings(data)).catch(() => {});
    api.get("/admin/rewards/balances").then(({ data }) => setBalances(data)).catch(() => {});
    api.get("/admin/rewards/transactions").then(({ data }) => setCreditTxns(data)).catch(() => {});
  }, []);

  const saveRewardsSettings = async () => {
    try {
      const { data } = await api.put("/admin/rewards/settings", {
        earn_rate_percent: Number(rewardsSettings.earn_rate_percent),
        referral_reward: Number(rewardsSettings.referral_reward),
        welcome_credit: Number(rewardsSettings.welcome_credit),
        max_redemption_percent: Number(rewardsSettings.max_redemption_percent),
      });
      setRewardsSettings(data);
      toast.success("Rewards settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitAdjust = async () => {
    const amount = Number(adjustAmount);
    if (!amount) { toast.error("Enter a non-zero amount"); return; }
    try {
      await api.post("/admin/rewards/adjust", { user_id: adjustUser.id, amount, note: adjustNote });
      toast.success(`${amount > 0 ? "Added" : "Deducted"} $${Math.abs(amount).toFixed(2)} for ${adjustUser.email}`);
      setAdjustUser(null); setAdjustAmount(""); setAdjustNote("");
      loadRewards();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const refreshOpenCount = useCallback(() => {
    api.get("/admin/tickets", { params: { status: "open" } })
      .then(({ data }) => setOpenTicketCount(data.length))
      .catch(() => {});
  }, []);

  const loadTickets = useCallback(() => {
    const params = {};
    if (ticketStatusFilter !== "all") params.status = ticketStatusFilter;
    if (ticketProductFilter !== "all") params.product = ticketProductFilter;
    api.get("/admin/tickets", { params }).then(({ data }) => setTickets(data)).catch(() => {});
  }, [ticketStatusFilter, ticketProductFilter]);

  const load = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/promos").then(({ data }) => setPromos(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadCategories(); loadReviews(); }, [load, loadCategories, loadReviews]);
  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => { refreshOpenCount(); }, [refreshOpenCount]);
  useEffect(() => { if (tab === "rewards") loadRewards(); }, [tab, loadRewards]);
  useEffect(() => { if (tab === "weeklydrop") loadDrop(); }, [tab, loadDrop]);

  const openNew = () => { setEditing({ ...EMPTY }); setFormErrors({}); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p }); setFormErrors({}); setOpen(true); };
  const openNewPromo = () => { setEditingPromo({ ...EMPTY_PROMO }); setPromoOpen(true); };
  const openEditPromo = (p) => {
    setEditingPromo({
      ...p,
      expires_at: p.expires_at ? String(p.expires_at).slice(0, 10) : "",
      max_uses: p.max_uses == null ? "" : p.max_uses,
      once_per_customer: !!p.once_per_customer,
    });
    setPromoOpen(true);
  };
  const openOrder = (o) => { setViewOrder(o); setOrderOpen(true); };
  const openTicket = (t) => { setViewTicket(t); setResponseText(""); setTicketOpen(true); };

  const changeTicketStatus = async (id, status) => {
    try {
      await api.put(`/admin/tickets/${id}/status`, { status });
      toast.success("Status updated");
      loadTickets();
      refreshOpenCount();
      setViewTicket((v) => (v && v.id === id ? { ...v, status } : v));
    } catch { toast.error("Could not update status"); }
  };

  const sendResponse = async () => {
    if (!responseText.trim() || !viewTicket) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/tickets/${viewTicket.id}/respond`, { message: responseText.trim() });
      toast.success("Response sent to customer");
      setViewTicket(data);
      setResponseText("");
      loadTickets();
      refreshOpenCount();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send response");
    } finally {
      setSending(false);
    }
  };

  const savePromo = async () => {
    const payload = {
      code: editingPromo.code,
      description: editingPromo.description || "",
      discount_percent: Number(editingPromo.discount_percent),
      min_clones: Number(editingPromo.min_clones),
      active: !!editingPromo.active,
      expires_at: editingPromo.expires_at ? editingPromo.expires_at : null,
      max_uses: editingPromo.max_uses === "" || editingPromo.max_uses == null ? null : Number(editingPromo.max_uses),
      once_per_customer: !!editingPromo.once_per_customer,
    };
    try {
      if (editingPromo.id) {
        await api.put(`/admin/promos/${editingPromo.id}`, payload);
        toast.success("Promo updated");
      } else {
        await api.post("/admin/promos", payload);
        toast.success("Promo created");
      }
      setPromoOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const delPromo = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    await api.delete(`/admin/promos/${id}`);
    toast.success("Promo deleted");
    load();
  };

  const validateProduct = () => {
    const e = {};
    if (!editing.name || !String(editing.name).trim()) e.name = "Name is required.";
    const price = Number(editing.price);
    if (editing.price === "" || editing.price == null || isNaN(price)) e.price = "Price is required.";
    else if (price <= 0) e.price = "Price must be greater than 0.";
    const stock = Number(editing.stock);
    if (editing.stock === "" || editing.stock == null || isNaN(stock)) e.stock = "Stock is required.";
    else if (stock < 0) e.stock = "Stock can't be negative.";
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validateProduct()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    const payload = {
      ...editing,
      price: Number(editing.price),
      stock: Number(editing.stock),
      genetics: editing.lineage || editing.genetics || "",
    };
    try {
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const changeStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Order updated");
    load();
  };

  const statCards = [
    { icon: DollarSign, label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—" },
    { icon: ShoppingCart, label: "Orders", value: stats?.orders ?? "—" },
    { icon: Boxes, label: "Products", value: stats?.products ?? "—" },
    { icon: Users, label: "Customers", value: stats?.customers ?? "—" },
  ];

  const inputCls = "w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2.5 transition-colors placeholder:text-slate-600";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <Seo title="Admin Dashboard | Dark Growers Genetics" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="label-caps text-emerald-500 mb-2">Control Room</p>
          <h1 className="font-display text-5xl text-slate-100">Admin Dashboard</h1>
        </div>
        {tab === "products" && (
          <button data-testid="add-product-button" onClick={openNew} className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors px-6 py-3 text-sm font-medium">
            <Plus size={16} /> New Product
          </button>
        )}
        {tab === "promos" && (
          <button data-testid="add-promo-button" onClick={openNewPromo} className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors px-6 py-3 text-sm font-medium">
            <Plus size={16} /> New Promo Code
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-10">
        {statCards.map((s) => (
          <div key={s.label} className="bg-[#0F0F0F] p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon size={18} className="text-emerald-500 mb-3" />
            <p className="text-2xl text-slate-100">{s.value}</p>
            <p className="label-caps text-slate-500 mt-1 text-[0.6rem]">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-8">
        {["products", "images", "categories", "reviews", "orders", "weeklydrop", "promos", "tickets", "rewards"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`label-caps px-6 py-3 border-b-2 -mb-px transition-colors ${
              tab === t ? "border-emerald-500 text-emerald-400" : "border-transparent text-slate-500 hover:text-slate-300"
            }`}
          >
            {t === "weeklydrop" ? "Weekly Drop" : t === "images" ? "Images" : t}
            {t === "tickets" && openTicketCount > 0 && (
              <span data-testid="open-tickets-badge" className="ml-2 inline-flex items-center justify-center bg-emerald-500 text-[#050505] text-[0.65rem] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 align-middle">
                {openTicketCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="border border-white/5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <img src={productImage(p.image_url)} onError={onImgError} alt="" className="w-9 h-9 object-cover bg-black border border-white/5" />
                    <span className="text-slate-200">{p.name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 capitalize">{p.category}</td>
                  <td className="px-4 py-3 text-slate-400 capitalize">{p.strain_type}</td>
                  <td className="px-4 py-3 text-slate-200">${p.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-400">{p.stock}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="text-slate-400 hover:text-emerald-400"><Pencil size={15} /></button>
                      <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "images" && (
        <div data-testid="image-assigner-tab">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <p className="text-sm text-slate-400 flex-1 min-w-[220px]">Give each strain its own photo. Drag &amp; drop or click a tile to upload — images are auto-resized &amp; compressed for fast loading.</p>
            <div className="flex gap-1">
              {["all", "clones", "seeds"].map((c) => (
                <button
                  key={c}
                  data-testid={`img-cat-${c}`}
                  onClick={() => setImgCat(c)}
                  className={`label-caps text-[0.6rem] px-3 py-2 border transition-colors ${imgCat === c ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-white/10 text-slate-500 hover:text-slate-300"}`}
                >
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
            <input
              data-testid="img-search"
              placeholder="Search strain…"
              value={imgSearch}
              onChange={(e) => setImgSearch(e.target.value)}
              className="bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2 placeholder:text-slate-600 w-48"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="image-assigner-grid">
            {products
              .filter((p) => imgCat === "all" || p.category === imgCat)
              .filter((p) => p.name.toLowerCase().includes(imgSearch.trim().toLowerCase()))
              .map((p) => (
                <div key={p.id} className="border border-white/10 bg-[#0F0F0F]" data-testid={`img-card-${p.id}`}>
                  <div
                    data-testid={`img-drop-${p.id}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); uploadRowImage(p, e.dataTransfer.files?.[0]); }}
                    onClick={() => document.getElementById(`img-input-${p.id}`)?.click()}
                    className="relative aspect-square bg-black cursor-pointer group overflow-hidden"
                  >
                    <img src={productImage(p.image_url)} onError={onImgError} alt={p.name} data-testid={`img-thumb-${p.id}`} className="w-full h-full object-cover" />
                    <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/70 text-center transition-opacity ${imgUploadingId === p.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                      {imgUploadingId === p.id ? (
                        <p className="text-emerald-400 text-xs" data-testid={`img-uploading-${p.id}`}>Uploading…</p>
                      ) : (
                        <>
                          <UploadCloud size={20} className="text-emerald-400" />
                          <p className="text-slate-200 text-[0.7rem] px-2">Drop or click to replace</p>
                        </>
                      )}
                    </div>
                  </div>
                  <input id={`img-input-${p.id}`} data-testid={`img-input-${p.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadRowImage(p, e.target.files?.[0])} />
                  <div className="px-3 py-2.5">
                    <p className="text-slate-200 text-sm truncate">{p.name}</p>
                    <p className="label-caps text-slate-500 text-[0.55rem] mt-0.5 capitalize">{p.category}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      {tab === "categories" && (
        <div data-testid="categories-tab">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-400 max-w-2xl">Data-driven clone categories. Order (lowest first) drives the nav dropdown and homepage pills. Deactivate to hide seasonally without losing clone tags.</p>
            <button data-testid="add-category-button" onClick={openNewCat} className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 px-5 py-2.5 text-sm font-medium whitespace-nowrap">
              <Plus size={16} /> Add Category
            </button>
          </div>
          <div className="border border-white/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="categories-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Clones</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminCategories.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No categories yet.</td></tr>
                ) : adminCategories.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`category-row-${c.slug}`}>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        defaultValue={c.sort_order}
                        data-testid={`cat-order-${c.slug}`}
                        onBlur={(e) => saveCatOrder(c, e.target.value)}
                        className="w-16 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-2 py-1.5"
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-200 uppercase tracking-wide">{c.name}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{c.slug}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{c.description || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 text-slate-400" data-testid={`cat-count-${c.slug}`}>{c.clone_count}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`cat-active-${c.slug}`}
                        onClick={() => toggleCatActive(c)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${c.active ? "bg-emerald-500" : "bg-white/15"}`}
                        title={c.active ? "Active — visible in nav & homepage" : "Hidden"}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${c.active ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`cat-bulk-${c.slug}`} onClick={() => openBulkTag(c)} className="text-xs px-3 py-1.5 border border-emerald-500/40 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 font-medium transition-colors whitespace-nowrap">Tag clones</button>
                        <button data-testid={`cat-edit-${c.slug}`} onClick={() => openEditCat(c)} className="text-slate-400 hover:text-emerald-400"><Pencil size={15} /></button>
                        <button data-testid={`cat-delete-${c.slug}`} onClick={() => delCategory(c)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tab === "reviews" && (
        <div data-testid="reviews-tab">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-400 max-w-2xl">Text reviews shown in the homepage "What Growers Are Saying" carousel. Order (lowest first) sets carousel position. Deactivate to hide without deleting.</p>
            <button data-testid="add-review-button" onClick={openNewReview} className="flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 px-5 py-2.5 text-sm font-medium whitespace-nowrap">
              <Plus size={16} /> Add Review
            </button>
          </div>
          <div className="border border-white/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="reviews-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Quote</th>
                  <th className="px-4 py-3">Author</th>
                  <th className="px-4 py-3">Rating</th>
                  <th className="px-4 py-3">Strain</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No reviews yet.</td></tr>
                ) : reviews.map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`review-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        defaultValue={r.display_order}
                        data-testid={`review-order-${r.id}`}
                        onBlur={(e) => saveReviewOrder(r, e.target.value)}
                        className="w-16 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-2 py-1.5"
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-300 max-w-md"><span className="line-clamp-2">{r.quote}</span></td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{r.author_handle || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`review-rating-${r.id}`}>
                      {r.rating ? <span className="text-emerald-400 text-xs">{"★".repeat(r.rating)}<span className="text-slate-700">{"★".repeat(5 - r.rating)}</span></span> : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{r.strain || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`review-active-${r.id}`}
                        onClick={() => toggleReviewActive(r)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${r.is_active ? "bg-emerald-500" : "bg-white/15"}`}
                        title={r.is_active ? "Active — visible on homepage" : "Hidden"}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${r.is_active ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`review-edit-${r.id}`} onClick={() => openEditReview(r)} className="text-slate-400 hover:text-emerald-400"><Pencil size={15} /></button>
                        <button data-testid={`review-delete-${r.id}`} onClick={() => delReview(r)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        <div className="border border-white/5 overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0F0F0F] border-b border-white/10">
            {["all", "awaiting", "preorder", "ready"].map((f) => (
              <button
                key={f}
                data-testid={`orders-filter-${f}`}
                onClick={() => setOrderFilter(f)}
                className={`text-xs px-3 py-1.5 capitalize transition-colors ${orderFilter === f ? "bg-emerald-600 text-white" : "border border-white/15 text-slate-400 hover:text-white"}`}
              >
                {f === "all" ? "All Orders" : f === "awaiting" ? "Awaiting Payment" : f === "preorder" ? "Preorders" : "Ready to Ship"}
              </button>
            ))}
          </div>
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Details</th>
              </tr>
            </thead>
            <tbody>
              {orders.filter((o) => orderFilter === "all" || (orderFilter === "awaiting" ? o.payment_status === "awaiting" : orderFilter === "preorder" ? o.is_preorder : !o.is_preorder)).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No orders in this view.</td></tr>
              ) : orders.filter((o) => orderFilter === "all" || (orderFilter === "awaiting" ? o.payment_status === "awaiting" : orderFilter === "preorder" ? o.is_preorder : !o.is_preorder)).map((o) => (
                <tr key={o.id} className="border-t border-white/5" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 text-slate-300">
                    {o.payment?.invoice || o.id.slice(-6)}
                    {o.is_preorder && (
                      <span data-testid={`order-preorder-badge-${o.id}`} className="ml-2 label-caps text-[0.55rem] bg-amber-500/20 text-amber-400 border border-amber-500/40 px-1.5 py-0.5 align-middle">
                        {o.is_mixed ? "Mixed" : "Preorder"}
                      </span>
                    )}
                    {o.payment_status === "awaiting" && (() => {
                      const age = orderAgeDays(o.created_at);
                      const cls = age >= 7 ? "bg-red-500/25 text-red-300 border-red-500/50" : age >= 3 ? "bg-orange-500/20 text-orange-300 border-orange-500/50" : "bg-yellow-500/15 text-yellow-300 border-yellow-500/40";
                      const label = age >= 7 ? "Eligible for Cancellation" : age >= 3 ? "Payment Overdue" : "Awaiting Payment";
                      return <div className="mt-1"><span data-testid={`order-payment-badge-${o.id}`} className={`label-caps text-[0.55rem] border px-1.5 py-0.5 ${cls}`}>{label}</span></div>;
                    })()}
                    <div className="text-[0.65rem] text-slate-600 mt-1 capitalize">{(o.payment?.method || "cashapp") === "venmo" ? "Venmo" : "Cash App"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{o.user_email}</td>
                  <td className="px-4 py-3 text-slate-400">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                  <td className="px-4 py-3 text-slate-200">${o.total.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    {o.payment_status === "awaiting" ? (
                      <button
                        data-testid={`confirm-payment-${o.id}`}
                        onClick={() => confirmPayment(o.id)}
                        className="bg-emerald-600 text-white hover:bg-emerald-500 transition-colors text-xs font-medium px-3 py-2"
                      >
                        Confirm Payment
                      </button>
                    ) : (
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger data-testid={`order-status-${o.id}`} className="w-36 bg-[#050505] border-white/10 text-slate-200 capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{STATUS_LABELS[s] || s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`view-order-${o.id}`} onClick={() => openOrder(o)} className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm">
                      <Eye size={15} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "weeklydrop" && (
        <div className="space-y-8" data-testid="weeklydrop-tab">
          <div className="border border-white/10 bg-[#0F0F0F] p-6">
            <h3 className="font-display text-2xl text-slate-100 mb-1 flex items-center gap-2"><Sparkles size={20} className="text-emerald-500" /> This Week's Theme</h3>
            <p className="text-xs text-slate-500 mb-4">Free-text banner shown on the homepage &amp; "Cuts Available This Week" page (e.g. Exotic Week, Gassy Week, OG Week).</p>
            <div className="flex gap-2 max-w-lg">
              <input data-testid="drop-theme-input" className={inputCls} value={dropTheme} onChange={(e) => setDropTheme(e.target.value)} placeholder="e.g. Gassy Week" />
              <button data-testid="save-drop-theme" onClick={saveDropTheme} disabled={dropSaving} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium whitespace-nowrap disabled:opacity-50">Save Theme</button>
            </div>
          </div>

          <div className="border border-white/5 overflow-x-auto">
            <div className="px-4 py-3 bg-[#0F0F0F] border-b border-white/10 flex items-center gap-2 flex-wrap">
              <Boxes size={16} className="text-emerald-500" /><span className="label-caps text-slate-300 text-xs">Clone Availability</span>
              <span className="text-xs text-slate-500 ml-2">Ready-to-Ship cuts appear in this week's drop. Everything else shows as Preorder.</span>
            </div>
            <table className="w-full text-sm" data-testid="drop-clones-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Strain</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3">Preorder Cap</th>
                  <th className="px-4 py-3">Waitlist</th>
                  <th className="px-4 py-3 text-right">Availability</th>
                </tr>
              </thead>
              <tbody>
                {products.filter((p) => p.category === "clones").length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No clones yet.</td></tr>
                ) : products.filter((p) => p.category === "clones").map((p) => {
                  const ready = (p.availability || "preorder") === "ready";
                  const wlCount = waitlist[p.id]?.count || 0;
                  return (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`drop-clone-row-${p.id}`}>
                      <td className="px-4 py-3 text-slate-200">{p.name}</td>
                      <td className="px-4 py-3 text-slate-400">{p.stock}</td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          data-testid={`drop-cap-${p.id}`}
                          defaultValue={p.max_preorders ?? ""}
                          placeholder="unlimited"
                          onBlur={(e) => saveCap(p, e.target.value)}
                          className="w-24 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-2 py-1.5"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span data-testid={`waitlist-count-${p.id}`} className={`label-caps text-[0.6rem] px-2 py-1 border ${wlCount > 0 ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-white/10 text-slate-500"}`}>
                            {wlCount} waiting
                          </span>
                          {wlCount > 0 && (
                            <button
                              data-testid={`notify-waitlist-${p.id}`}
                              onClick={() => notifyWaitlist(p)}
                              disabled={notifyingId === p.id}
                              className="text-xs px-3 py-1.5 border border-emerald-500/50 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              {notifyingId === p.id ? "Sending…" : "Notify waitlist"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`drop-toggle-${p.id}`}
                          onClick={() => toggleAvailability(p)}
                          className={`text-xs px-3 py-1.5 border font-medium transition-colors ${ready ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20" : "border-amber-500/40 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20"}`}
                        >
                          {ready ? "Ready to Ship" : "Preorder"} · flip
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tab === "promos" && (
        <div className="border border-white/5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="promos-table">
            <thead>
              <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Min. Clones</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No promo codes yet. Create one to run a sale.</td></tr>
              ) : promos.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`promo-row-${p.id}`}>
                  <td className="px-4 py-3 font-display text-lg text-emerald-400 tracking-wide">
                    {p.code}
                    {p.once_per_customer && <span className="ml-2 text-[0.6rem] text-slate-500 align-middle">1×/customer</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-200">{p.discount_percent}%</td>
                  <td className="px-4 py-3 text-slate-400">{p.min_clones}</td>
                  <td className="px-4 py-3 text-slate-400" data-testid={`promo-uses-${p.id}`}>
                    {p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{p.expires_at ? String(p.expires_at).slice(0, 10) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 border ${p.active ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-white/15 text-slate-500"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{p.description}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`edit-promo-${p.id}`} onClick={() => openEditPromo(p)} className="text-slate-400 hover:text-emerald-400"><Pencil size={15} /></button>
                      <button data-testid={`delete-promo-${p.id}`} onClick={() => delPromo(p.id)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "tickets" && (
        <div>
          <div className="flex flex-wrap gap-3 mb-5">
            <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
              <SelectTrigger data-testid="ticket-status-filter" className="w-44 bg-[#050505] border-white/10 text-slate-200 capitalize">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                <SelectItem value="all">All statuses</SelectItem>
                {TICKET_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ticketProductFilter} onValueChange={setTicketProductFilter}>
              <SelectTrigger data-testid="ticket-product-filter" className="w-60 bg-[#050505] border-white/10 text-slate-200">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-h-72">
                <SelectItem value="all">All products</SelectItem>
                {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-white/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="tickets-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">View</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No support tickets.</td></tr>
                ) : tickets.map((t) => (
                  <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`ticket-row-${t.id}`}>
                    <td className="px-4 py-3"><p className="text-slate-200">{t.name}</p><p className="text-slate-500 text-xs">{t.email}</p></td>
                    <td className="px-4 py-3 text-slate-400">{t.product}</td>
                    <td className="px-4 py-3 text-slate-300 max-w-[16rem] truncate">{t.subject}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <Select value={t.status} onValueChange={(v) => changeTicketStatus(t.id, v)}>
                        <SelectTrigger data-testid={`ticket-status-${t.id}`} className="w-36 bg-[#050505] border-white/10 text-slate-200 capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                          {TICKET_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`view-ticket-${t.id}`} onClick={() => openTicket(t)} className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm">
                        <Eye size={15} /> Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "rewards" && (
        <div className="space-y-8" data-testid="rewards-tab">
          {/* Settings */}
          {rewardsSettings && (
            <div className="border border-white/10 bg-[#0F0F0F] p-6">
              <h3 className="font-display text-2xl text-slate-100 mb-1 flex items-center gap-2"><Gift size={20} className="text-emerald-500" /> Program Settings</h3>
              <p className="text-xs text-slate-500 mb-5">Changes apply to future earnings and redemptions.</p>
              <div className="grid sm:grid-cols-4 gap-4">
                <div>
                  <label className="label-caps text-slate-400 text-xs">Earn Rate %</label>
                  <input type="number" min="0" data-testid="settings-earn-rate" className={`${inputCls} mt-1`} value={rewardsSettings.earn_rate_percent} onChange={(e) => setRewardsSettings({ ...rewardsSettings, earn_rate_percent: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps text-slate-400 text-xs">Referral Reward $</label>
                  <input type="number" min="0" data-testid="settings-referral-reward" className={`${inputCls} mt-1`} value={rewardsSettings.referral_reward} onChange={(e) => setRewardsSettings({ ...rewardsSettings, referral_reward: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps text-slate-400 text-xs">Welcome Credit $</label>
                  <input type="number" min="0" data-testid="settings-welcome-credit" className={`${inputCls} mt-1`} value={rewardsSettings.welcome_credit} onChange={(e) => setRewardsSettings({ ...rewardsSettings, welcome_credit: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps text-slate-400 text-xs">Max Redemption %</label>
                  <input type="number" min="0" max="100" data-testid="settings-max-redemption" className={`${inputCls} mt-1`} value={rewardsSettings.max_redemption_percent} onChange={(e) => setRewardsSettings({ ...rewardsSettings, max_redemption_percent: e.target.value })} />
                </div>
              </div>
              <button data-testid="save-rewards-settings" onClick={saveRewardsSettings} className="mt-5 bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Save Settings</button>
            </div>
          )}

          {/* Balances */}
          <div className="border border-white/5 overflow-x-auto">
            <div className="px-4 py-3 bg-[#0F0F0F] flex items-center gap-2 border-b border-white/10">
              <Wallet size={16} className="text-emerald-500" /><span className="label-caps text-slate-300 text-xs">Customer Balances</span>
            </div>
            <table className="w-full text-sm" data-testid="balances-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Referral Code</th>
                  <th className="px-4 py-3">Referrals</th>
                  <th className="px-4 py-3">Balance</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {balances.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No customers yet.</td></tr>
                ) : balances.map((b) => (
                  <tr key={b.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`balance-row-${b.id}`}>
                    <td className="px-4 py-3"><p className="text-slate-200">{b.name}</p><p className="text-slate-500 text-xs">{b.email}</p></td>
                    <td className="px-4 py-3 text-emerald-400 font-mono text-xs">{b.referral_code}</td>
                    <td className="px-4 py-3 text-slate-400">{b.referral_count}</td>
                    <td className="px-4 py-3 text-emerald-400 font-medium" data-testid={`balance-amount-${b.id}`}>${b.balance.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`adjust-credit-${b.id}`} onClick={() => { setAdjustUser(b); setAdjustAmount(""); setAdjustNote(""); }} className="text-emerald-400 hover:text-emerald-300 text-sm">Adjust</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Transactions */}
          <div className="border border-white/5 overflow-x-auto">
            <div className="px-4 py-3 bg-[#0F0F0F] border-b border-white/10"><span className="label-caps text-slate-300 text-xs">Credit Transaction Log</span></div>
            <table className="w-full text-sm" data-testid="credit-txns-table">
              <thead>
                <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Note / Referred</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {creditTxns.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No credit activity yet.</td></tr>
                ) : creditTxns.map((t, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-slate-300">{t.user_email || "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{t.type.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{t.related_user_email ? `${t.note} → ${t.related_user_email}` : t.note}</td>
                    <td className={`px-4 py-3 text-right font-medium ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.amount >= 0 ? "+" : "-"}${Math.abs(t.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100">
              {editing?.id ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input placeholder="Name" data-testid="form-name" className={`${inputCls} ${formErrors.name ? "border-red-500 focus:border-red-500" : ""}`} value={editing.name} onChange={(e) => { setEditing({ ...editing, name: e.target.value }); if (formErrors.name) setFormErrors({ ...formErrors, name: undefined }); }} />
              {formErrors.name && <p data-testid="form-name-error" className="text-red-400 text-xs -mt-1">{formErrors.name}</p>}
              <div className="grid grid-cols-2 gap-3">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="form-category" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                    <SelectItem value="clones">Clones</SelectItem>
                    <SelectItem value="seeds">Seeds</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={editing.strain_type} onValueChange={(v) => setEditing({ ...editing, strain_type: v })}>
                  <SelectTrigger data-testid="form-type" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                    <SelectItem value="indica">Indica</SelectItem>
                    <SelectItem value="sativa">Sativa</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input type="number" placeholder="Price" data-testid="form-price" className={`${inputCls} ${formErrors.price ? "border-red-500 focus:border-red-500" : ""}`} value={editing.price} onChange={(e) => { setEditing({ ...editing, price: e.target.value }); if (formErrors.price) setFormErrors({ ...formErrors, price: undefined }); }} />
                  {formErrors.price && <p data-testid="form-price-error" className="text-red-400 text-xs mt-1">{formErrors.price}</p>}
                </div>
                <div>
                  <input type="number" placeholder="Stock" data-testid="form-stock" className={`${inputCls} ${formErrors.stock ? "border-red-500 focus:border-red-500" : ""}`} value={editing.stock} onChange={(e) => { setEditing({ ...editing, stock: e.target.value }); if (formErrors.stock) setFormErrors({ ...formErrors, stock: undefined }); }} />
                  {formErrors.stock && <p data-testid="form-stock-error" className="text-red-400 text-xs mt-1">{formErrors.stock}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="THC (e.g. 24-30%)" className={inputCls} value={editing.thc || ""} onChange={(e) => setEditing({ ...editing, thc: e.target.value })} />
                <input placeholder="Flowering time" className={inputCls} value={editing.flowering_time || ""} onChange={(e) => setEditing({ ...editing, flowering_time: e.target.value })} />
              </div>
              <input placeholder="Lineage (e.g. Sunset Sherbet x Thin Mint GSC)" data-testid="form-lineage" className={inputCls} value={editing.lineage || ""} onChange={(e) => setEditing({ ...editing, lineage: e.target.value })} />
              <input placeholder="Breeder (e.g. Seed Junky Genetics)" data-testid="form-breeder" className={inputCls} value={editing.breeder || ""} onChange={(e) => setEditing({ ...editing, breeder: e.target.value })} />
              <input placeholder="Pack size" className={inputCls} value={editing.pack_size || ""} onChange={(e) => setEditing({ ...editing, pack_size: e.target.value })} />
              <div>
                <label className="label-caps text-slate-500 text-[0.6rem] block mb-2">Product Image</label>
                <div
                  data-testid="image-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleImageFile(e.dataTransfer.files?.[0]); }}
                  onClick={() => document.getElementById("product-image-input")?.click()}
                  className="border border-dashed border-white/20 hover:border-emerald-500/60 bg-[#050505] cursor-pointer transition-colors p-4 flex items-center gap-4"
                >
                  <div className="w-20 h-20 bg-black border border-white/10 shrink-0 overflow-hidden flex items-center justify-center">
                    <img src={productImage(editing.image_url)} onError={onImgError} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 text-sm">
                    {uploading ? (
                      <p className="text-emerald-400" data-testid="image-uploading">Uploading &amp; optimizing…</p>
                    ) : (
                      <>
                        <p className="text-slate-300 flex items-center gap-2"><UploadCloud size={16} className="text-emerald-500" /> Drag &amp; drop or click to upload</p>
                        <p className="text-slate-500 text-xs mt-1">JPG, PNG, WEBP or HEIC — auto-resized for fast loading.</p>
                      </>
                    )}
                  </div>
                  {editing.image_url && !uploading && (
                    <button type="button" data-testid="image-remove" onClick={(e) => { e.stopPropagation(); setEditing({ ...editing, image_url: "" }); }} className="text-slate-500 hover:text-red-400"><X size={16} /></button>
                  )}
                </div>
                <input id="product-image-input" data-testid="image-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFile(e.target.files?.[0])} />
                <input placeholder="…or paste an image URL (optional)" data-testid="form-image-url" className={`${inputCls} mt-2`} value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
              </div>
              <textarea placeholder="Description" rows={3} className={inputCls} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              {editing.category === "clones" && adminCategories.filter((c) => c.active).length > 0 && (
                <div data-testid="form-clone-categories">
                  <label className="label-caps text-slate-500 text-[0.6rem] block mb-2">Clone Categories</label>
                  <div className="flex flex-wrap gap-2">
                    {adminCategories.filter((c) => c.active).map((c) => {
                      const on = (editing.categories || []).includes(c.slug);
                      return (
                        <button
                          type="button"
                          key={c.slug}
                          data-testid={`form-cat-${c.slug}`}
                          onClick={() => {
                            const cur = editing.categories || [];
                            setEditing({ ...editing, categories: on ? cur.filter((s) => s !== c.slug) : [...cur, c.slug] });
                          }}
                          className={`label-caps text-[0.6rem] px-3 py-1.5 border transition-colors ${on ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-white/10 text-slate-500 hover:text-slate-300"}`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                Featured on homepage
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-product" onClick={save} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100">
              {catEditing?.id ? "Edit Category" : "New Category"}
            </DialogTitle>
          </DialogHeader>
          {catEditing && (
            <div className="space-y-3">
              <div>
                <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Name</label>
                <input
                  data-testid="cat-form-name"
                  placeholder="e.g. GAS"
                  className={inputCls}
                  value={catEditing.name}
                  onChange={(e) => setCatEditing({ ...catEditing, name: e.target.value, slug: catSlugTouched ? catEditing.slug : slugify(e.target.value) })}
                />
              </div>
              <div>
                <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Slug (URL)</label>
                <input
                  data-testid="cat-form-slug"
                  placeholder="gas"
                  className={`${inputCls} font-mono`}
                  value={catEditing.slug}
                  onChange={(e) => { setCatSlugTouched(true); setCatEditing({ ...catEditing, slug: e.target.value }); }}
                />
              </div>
              <div>
                <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Description (one line)</label>
                <input
                  data-testid="cat-form-description"
                  placeholder="Shown on the category page"
                  className={inputCls}
                  value={catEditing.description || ""}
                  onChange={(e) => setCatEditing({ ...catEditing, description: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input type="checkbox" data-testid="cat-form-active" checked={catEditing.active} onChange={(e) => setCatEditing({ ...catEditing, active: e.target.checked })} />
                Active (visible in nav &amp; homepage)
              </label>
              {catError && <p data-testid="cat-form-error" className="text-red-400 text-xs">{catError}</p>}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setCatOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-category" onClick={saveCategory} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100 flex items-center gap-2">
              <Quote size={22} className="text-emerald-500" /> {reviewEditing?.id ? "Edit Review" : "Add Review"}
            </DialogTitle>
          </DialogHeader>
          {reviewEditing && (
            <div className="space-y-4">
              <div>
                <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Review quote *</label>
                <textarea
                  data-testid="review-form-quote"
                  rows={4}
                  placeholder="What the grower said…"
                  className={inputCls}
                  value={reviewEditing.quote || ""}
                  onChange={(e) => setReviewEditing({ ...reviewEditing, quote: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Author / handle</label>
                  <input
                    data-testid="review-form-author"
                    placeholder="Verified Grower"
                    className={inputCls}
                    value={reviewEditing.author_handle || ""}
                    onChange={(e) => setReviewEditing({ ...reviewEditing, author_handle: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Strain (optional)</label>
                  <input
                    data-testid="review-form-strain"
                    placeholder="e.g. Gelato 41"
                    className={inputCls}
                    value={reviewEditing.strain || ""}
                    onChange={(e) => setReviewEditing({ ...reviewEditing, strain: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Star rating</label>
                  <select
                    data-testid="review-form-rating"
                    className={inputCls}
                    value={reviewEditing.rating ?? ""}
                    onChange={(e) => setReviewEditing({ ...reviewEditing, rating: e.target.value === "" ? null : Number(e.target.value) })}
                  >
                    <option value="">No stars</option>
                    <option value="5">★★★★★ (5)</option>
                    <option value="4">★★★★ (4)</option>
                    <option value="3">★★★ (3)</option>
                    <option value="2">★★ (2)</option>
                    <option value="1">★ (1)</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps text-slate-500 text-[0.6rem] block mb-1">Display order (lowest first)</label>
                  <input
                    type="number"
                    data-testid="review-form-order"
                    placeholder="auto"
                    className={inputCls}
                    value={reviewEditing.display_order ?? ""}
                    onChange={(e) => setReviewEditing({ ...reviewEditing, display_order: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input type="checkbox" data-testid="review-form-active" checked={reviewEditing.is_active} onChange={(e) => setReviewEditing({ ...reviewEditing, is_active: e.target.checked })} />
                Active (visible on homepage)
              </label>
              {reviewError && <p data-testid="review-form-error" className="text-red-400 text-xs">{reviewError}</p>}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setReviewOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-review" onClick={saveReview} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100">
              Tag clones — <span className="text-emerald-400 uppercase">{bulkCat?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {bulkCat && (
            <div>
              <p className="text-xs text-slate-500 mb-3">Check the clones that belong to <b className="text-slate-300 uppercase">{bulkCat.name}</b>. Checked = tagged. Unchecking removes the tag. This only affects the {bulkCat.name} tag — other categories on a clone are untouched.</p>
              <input
                data-testid="bulk-search"
                placeholder="Search clones…"
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2 placeholder:text-slate-600 mb-1"
              />
              <div className="flex items-center justify-between text-[0.7rem] text-slate-500 mb-2 px-1">
                <span data-testid="bulk-selected-count">{bulkSelected.size} selected</span>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBulkSelected(new Set(products.filter((p) => p.category === "clones" && p.name.toLowerCase().includes(bulkSearch.trim().toLowerCase())).map((p) => p.id)))}
                    className="text-emerald-400 hover:text-emerald-300"
                  >Select all shown</button>
                  <button onClick={() => setBulkSelected(new Set())} className="text-slate-400 hover:text-slate-200">Clear</button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border border-white/10 divide-y divide-white/5" data-testid="bulk-clone-list">
                {products
                  .filter((p) => p.category === "clones")
                  .filter((p) => p.name.toLowerCase().includes(bulkSearch.trim().toLowerCase()))
                  .map((p) => {
                    const on = bulkSelected.has(p.id);
                    const already = (p.categories || []).includes(bulkCat.slug);
                    return (
                      <label key={p.id} data-testid={`bulk-clone-${p.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] cursor-pointer">
                        <input type="checkbox" checked={on} onChange={() => toggleBulk(p.id)} className="accent-emerald-500" />
                        <span className="flex-1 text-sm text-slate-200 truncate">{p.name}</span>
                        {already && <span className="label-caps text-[0.55rem] text-emerald-400/70">tagged</span>}
                      </label>
                    );
                  })}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setBulkOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-bulk-tag" onClick={saveBulkTag} disabled={bulkSaving} className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 px-6 py-2.5 text-sm font-medium">
              {bulkSaving ? "Saving…" : "Save Tags"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustUser} onOpenChange={(o) => !o && setAdjustUser(null)}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100 flex items-center gap-2">
              <Wallet size={22} className="text-emerald-500" /> Adjust Credit
            </DialogTitle>
          </DialogHeader>
          {adjustUser && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">{adjustUser.name} · {adjustUser.email}</p>
              <p className="text-sm">Current balance: <span className="text-emerald-400 font-medium">${adjustUser.balance.toFixed(2)}</span></p>
              <div>
                <label className="label-caps text-slate-400 text-xs">Amount (use negative to deduct)</label>
                <input type="number" step="0.01" data-testid="adjust-amount" placeholder="e.g. 15 or -5" className={`${inputCls} mt-1`} value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
              </div>
              <div>
                <label className="label-caps text-slate-400 text-xs">Note (reason)</label>
                <input data-testid="adjust-note" placeholder="e.g. Goodwill credit for shipping delay" className={`${inputCls} mt-1`} value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAdjustUser(null)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-adjust" onClick={submitAdjust} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Apply</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100 flex items-center gap-2">
              <Tag size={22} className="text-emerald-500" />
              {editingPromo?.id ? "Edit Promo Code" : "New Promo Code"}
            </DialogTitle>
          </DialogHeader>
          {editingPromo && (
            <div className="space-y-3">
              <div>
                <label className="label-caps text-slate-400 text-xs">Code</label>
                <input placeholder="e.g. DARK25" data-testid="promo-form-code" className={`${inputCls} mt-1 uppercase tracking-wide`} value={editingPromo.code} onChange={(e) => setEditingPromo({ ...editingPromo, code: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps text-slate-400 text-xs">Discount %</label>
                  <input type="number" min="0" max="100" data-testid="promo-form-percent" className={`${inputCls} mt-1`} value={editingPromo.discount_percent} onChange={(e) => setEditingPromo({ ...editingPromo, discount_percent: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps text-slate-400 text-xs">Min. Clones</label>
                  <input type="number" min="0" data-testid="promo-form-min-clones" className={`${inputCls} mt-1`} value={editingPromo.min_clones} onChange={(e) => setEditingPromo({ ...editingPromo, min_clones: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-slate-500">Discount applies to the entire order. "Min. Clones" is the minimum number of clones required in the cart to use this code (e.g. 2 = more than 1 clone). Use 0 for no requirement.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps text-slate-400 text-xs">Expiry Date</label>
                  <input type="date" data-testid="promo-form-expires" className={`${inputCls} mt-1`} value={editingPromo.expires_at || ""} onChange={(e) => setEditingPromo({ ...editingPromo, expires_at: e.target.value })} />
                </div>
                <div>
                  <label className="label-caps text-slate-400 text-xs">Max Total Uses</label>
                  <input type="number" min="0" placeholder="Unlimited" data-testid="promo-form-max-uses" className={`${inputCls} mt-1`} value={editingPromo.max_uses} onChange={(e) => setEditingPromo({ ...editingPromo, max_uses: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-slate-500">Leave expiry blank for no end date. Leave max uses blank for unlimited redemptions.</p>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" data-testid="promo-form-once-per-customer" checked={!!editingPromo.once_per_customer} onChange={(e) => setEditingPromo({ ...editingPromo, once_per_customer: e.target.checked })} />
                Limit to one use per customer
              </label>
              <div>
                <label className="label-caps text-slate-400 text-xs">Description</label>
                <textarea rows={2} placeholder="Shown to customers in the promo popup" data-testid="promo-form-description" className={`${inputCls} mt-1`} value={editingPromo.description} onChange={(e) => setEditingPromo({ ...editingPromo, description: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" data-testid="promo-form-active" checked={editingPromo.active} onChange={(e) => setEditingPromo({ ...editingPromo, active: e.target.checked })} />
                Active (customers can use this code)
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setPromoOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Cancel</button>
            <button data-testid="save-promo" onClick={savePromo} className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100">Order Details</DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-6 text-sm">
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Invoice</p><p className="text-slate-200">{viewOrder.payment?.invoice || viewOrder.id.slice(-6)}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Customer</p><p className="text-slate-200">{viewOrder.user_email}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Status</p><p className="text-emerald-400 capitalize">{viewOrder.status}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Date</p><p className="text-slate-200">{viewOrder.created_at ? new Date(viewOrder.created_at).toLocaleString() : "—"}</p></div>
              </div>

              <div className="border-t border-white/10 pt-4" data-testid="order-shipping">
                <p className="label-caps text-emerald-500 mb-3">Ship To</p>
                {viewOrder.shipping ? (
                  <div className="text-slate-300 leading-relaxed">
                    <p className="text-slate-100">{viewOrder.shipping.full_name}</p>
                    <p>{viewOrder.shipping.address}</p>
                    <p>{viewOrder.shipping.city}, {viewOrder.shipping.state} {viewOrder.shipping.zip_code}</p>
                    {viewOrder.shipping.phone && <p>Phone: {viewOrder.shipping.phone}</p>}
                  </div>
                ) : <p className="text-slate-500">No shipping address on file.</p>}
              </div>

              <div className="border-t border-white/10 pt-4" data-testid="order-items">
                <p className="label-caps text-emerald-500 mb-3">Items Purchased</p>
                <div className="space-y-3">
                  {viewOrder.items.map((i, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {i.image_url && <img src={i.image_url} alt="" className="w-10 h-10 object-cover bg-black border border-white/5" />}
                      <div className="flex-1">
                        <p className="text-slate-200">{i.name}</p>
                        <p className="text-slate-500 text-xs">Qty {i.quantity} × ${i.price.toFixed(2)}</p>
                      </div>
                      <span className="text-slate-300">${(i.price * i.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 space-y-1.5">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="text-slate-300">${(viewOrder.subtotal ?? viewOrder.total).toFixed(2)}</span></div>
                {viewOrder.discount > 0 && (
                  <div className="flex justify-between"><span className="text-emerald-400">Discount{viewOrder.promo_code ? ` (${viewOrder.promo_code})` : ""}</span><span className="text-emerald-400">-${viewOrder.discount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="text-slate-300">{(viewOrder.shipping_fee ?? 0) === 0 ? "Free · Next-Day" : `$${viewOrder.shipping_fee.toFixed(2)}`}</span></div>
                <div className="flex justify-between text-base pt-2 border-t border-white/10 mt-1"><span className="text-slate-100">Total</span><span className="text-emerald-400">${viewOrder.total.toFixed(2)}</span></div>
                {viewOrder.payment?.method === "cashapp" ? (
                  <p className="text-xs text-slate-500 mt-2">Cash App payment to {viewOrder.payment.cashtag} · note: {viewOrder.payment.invoice}</p>
                ) : viewOrder.payment?.last4 ? (
                  <p className="text-xs text-slate-500 mt-2">Paid with card ending {viewOrder.payment.last4} · Txn {viewOrder.payment.transaction_id}</p>
                ) : null}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOrderOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-slate-100">Support Ticket</DialogTitle>
          </DialogHeader>
          {viewTicket && (
            <div className="space-y-5 text-sm">
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Customer</p><p className="text-slate-200">{viewTicket.name}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Email</p><p className="text-slate-200">{viewTicket.email}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Product</p><p className="text-emerald-400">{viewTicket.product}</p></div>
                <div><p className="label-caps text-slate-500 text-[0.6rem]">Date</p><p className="text-slate-200">{viewTicket.created_at ? new Date(viewTicket.created_at).toLocaleString() : "—"}</p></div>
              </div>

              <div className="flex items-center gap-3">
                <p className="label-caps text-slate-500 text-[0.6rem]">Status</p>
                <Select value={viewTicket.status} onValueChange={(v) => changeTicketStatus(viewTicket.id, v)}>
                  <SelectTrigger data-testid="ticket-detail-status" className="w-40 bg-[#050505] border-white/10 text-slate-200 capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-white/10 text-slate-200">
                    {TICKET_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-slate-100 font-medium">{viewTicket.subject}</p>
                <p className="text-slate-400 mt-2 leading-relaxed whitespace-pre-wrap">{viewTicket.message}</p>
              </div>

              {viewTicket.response && (
                <div className="border-l-2 border-emerald-500/50 pl-4">
                  <p className="label-caps text-emerald-500 text-[0.6rem] mb-1">Your Response</p>
                  <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{viewTicket.response}</p>
                </div>
              )}

              <div className="border-t border-white/10 pt-4">
                <p className="label-caps text-slate-400 text-xs mb-2">{viewTicket.response ? "Send another reply" : "Respond to customer"}</p>
                <textarea
                  data-testid="ticket-response-input"
                  rows={4}
                  placeholder="Type your reply — this emails the customer directly."
                  className="w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2.5 transition-colors placeholder:text-slate-600"
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-2">Reply is emailed to {viewTicket.email} and sets status to "Responded".</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setTicketOpen(false)} className="border border-white/15 text-slate-300 hover:text-white px-6 py-2.5 text-sm">Close</button>
            <button
              data-testid="send-ticket-response"
              onClick={sendResponse}
              disabled={!responseText.trim() || sending}
              className="bg-emerald-600 text-white hover:bg-emerald-500 px-6 py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send Response"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
